import { FormEvent, useEffect, useState, useTransition } from "react";
import { toast } from "sonner";

import { LOGIN_PATH } from "@buildpy/core/modules/onboarding/rules";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Label } from "@buildpy/ui/label";
import SectionCard from "@buildpy/ui/section-card";
import { BrandLogo } from "@buildpy/ui/brand-logo";
import { Link, navigateTo } from "@/lib/nav";
import { authCallbackOf, BOOT_HREF, getSupabase } from "@/lib/supabase";
import { resendSignupMail, setSignupPassword, signupStatus } from "@/api/actions/onboarding";
import ResetPasswordForm from "@/pages/(auth)/reset-password/reset-form";
import SignupSent from "@/modules/onboarding/ui/signup-sent";
import { RESEND_FAILED_MESSAGE } from "@/modules/onboarding/ui/signup-sent-state";
import SignupSteps from "@/modules/onboarding/ui/signup-steps";
import { ONBOARDING_PATH, SIGNUP_PATH } from "@/modules/plan/copy";
import { SET_PASSWORD_FAILED_MESSAGE, VERIFY_EXPIRED_BODY, VERIFY_EXPIRED_TITLE, VERIFY_PASSWORD_BODY, VERIFY_PASSWORD_TITLE, VERIFY_WAITING_BODY, VERIFY_WAITING_TITLE, type VerifyScreen, verifyScreenOf } from "./verify-state";

// 세션이 서 있으면 서버에 비밀번호가 남았는지 묻는다 — 못 물으면 세션 없음으로 본다(다시 받기 화면).
const sessionState = async (): Promise<{ hasSession: boolean; needsPassword: boolean }> => {
  const { data } = await getSupabase().auth.getSession();
  if (!data.session) return { hasSession: false, needsPassword: false };
  const status = await signupStatus();
  return status ? { hasSession: true, needsPassword: status.needsPassword } : { hasSession: false, needsPassword: false };
};

// 비밀번호를 정하면 회사 정보로 — 실패 문구는 서버 답 그대로(세션이 죽었으면 로그인 안내).
const SetPasswordForm = () => (
  <ResetPasswordForm
    onSave={async (password, confirm) => {
      try {
        const result = await setSignupPassword({ password, confirm });
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        navigateTo(ONBOARDING_PATH, true);
      } catch {
        toast.error(SET_PASSWORD_FAILED_MESSAGE);
      }
    }}
    pendingLabel="정하는 중…"
    submitLabel="비밀번호 정하기"
  />
);

// 이메일을 넣어 확인 메일 다시 보내기 — 보낸 뒤에는 보낸 화면(60초 대기).
const ResendByEmail = () => {
  const [email, setEmail] = useState<string | null>(null);
  const [pending, startTransition] = useTransition();
  if (email) return <SignupSent email={email} onChangeEmail={() => setEmail(null)} />;
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = String(new FormData(event.currentTarget).get("email") ?? "").trim().toLowerCase();
    if (!value) return;
    startTransition(async () => {
      try {
        const result = await resendSignupMail(value);
        if (!result.ok) {
          toast.error(result.message);
          return;
        }
        toast.success(result.message);
        setEmail(value);
      } catch {
        toast.error(RESEND_FAILED_MESSAGE);
      }
    });
  };
  return (
    <form className="flex flex-col gap-y-3" data-testid="verify-resend-form" onSubmit={onSubmit}>
      <div className="flex flex-col gap-y-1.5">
        <Label htmlFor="verify-email">가입한 이메일</Label>
        <Input autoComplete="email" id="verify-email" inputMode="email" maxLength={254} name="email" placeholder="you@company.com" required type="email" />
      </div>
      <Button disabled={pending} type="submit" variant="outline">
        {pending ? "보내는 중…" : "이 이메일로 다시 받기"}
      </Button>
    </form>
  );
};

export const SignupVerifyView = ({ screen }: { screen: VerifyScreen }) => (
  <main className="safe-top-fill flex min-h-screen items-center justify-center bg-background px-4 pb-4 pt-safe-4">
    <SectionCard as="div" className="flex w-full max-w-md flex-col gap-y-6" data-screen={screen} data-testid="signup-verify" padding="lg">
      <div className="flex flex-col gap-y-3">
        <Link aria-label="buildpy 첫 화면" className="flex" href="/">
          <BrandLogo size="lg" />
        </Link>
        <SignupSteps current="메일 확인" />
      </div>
      {screen === "password" ? (
        <>
          <div className="flex flex-col gap-y-2">
            <h1 className="text-lg font-semibold">{VERIFY_PASSWORD_TITLE}</h1>
            <p className="text-sm text-secondary-foreground">{VERIFY_PASSWORD_BODY}</p>
          </div>
          <SetPasswordForm />
        </>
      ) : screen === "checking" || screen === "onboarding" ? (
        <p className="text-sm text-muted-foreground">{screen === "onboarding" ? "확인됐습니다. 회사 정보 입력으로 넘어갑니다…" : "링크를 확인하는 중…"}</p>
      ) : (
        <>
          <div className="flex flex-col gap-y-2">
            <h1 className="text-lg font-semibold">{screen === "expired" ? VERIFY_EXPIRED_TITLE : VERIFY_WAITING_TITLE}</h1>
            <p className="text-sm text-secondary-foreground">{screen === "expired" ? VERIFY_EXPIRED_BODY : VERIFY_WAITING_BODY}</p>
          </div>
          <ResendByEmail />
          <p className="text-sm text-muted-foreground">
            이미 확인하셨으면{" "}
            <Link className="font-medium text-primary underline underline-offset-4 hover:text-foreground" href={LOGIN_PATH}>
              로그인
            </Link>
            {" · "}
            <Link className="underline underline-offset-4 hover:text-foreground" href={SIGNUP_PATH}>
              처음부터 다시 가입
            </Link>
          </p>
        </>
      )}
    </SectionCard>
  </main>
);

// 가입 확인 메일 링크가 닿는 곳(implicit #access_token&type=signup|magiclink) — 세션이 서면 비밀번호 정하기 → /onboarding.
const SignupVerifyPage = () => {
  const [screen, setScreen] = useState<VerifyScreen>("checking");

  useEffect(() => {
    let alive = true;
    const callback = authCallbackOf(BOOT_HREF);
    void sessionState()
      .then(
        (state) => verifyScreenOf(callback, state.hasSession, state.needsPassword),
        () => (callback.kind === "none" ? ("waiting" as const) : ("expired" as const)),
      )
      .then((next) => {
        if (!alive) return;
        // 토큰·오류가 붙은 주소를 기록에 남기지 않는다.
        window.history.replaceState(window.history.state, "", window.location.pathname);
        setScreen(next);
        if (next === "onboarding") navigateTo(ONBOARDING_PATH, true);
      });
    return () => {
      alive = false;
    };
  }, []);

  return <SignupVerifyView screen={screen} />;
};

export default SignupVerifyPage;
